import React, { useState, useMemo } from 'react';
import { SUPABASE_URL } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Plus, Edit2, Check, X, Download, FileSpreadsheet, FileText, CalendarPlus, Star, Loader2, Share2, Link2, Lock, Backpack } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { PackingList } from './PackingList';
import { getShootingQualityScore, getDarknessWindowMinutes, getQualityLabel } from '@/utils/shootingQualityScore';
import { TravelItineraryWithStops, useTravelItineraries } from '@/hooks/useTravelItineraries';
import { TripStopCard } from './TripStopCard';
import { TripStopDialog } from './TripStopDialog';
import { Suspense, lazy } from 'react';

const TripOverviewMap = lazy(() => import('./TripOverviewMap').then(m => ({ default: m.TripOverviewMap })));
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';
import { calculateMonthData, DayData, getVisibilityRating } from '@/utils/calendarData';
import { writeExcelFile, type ExcelSheet } from '@/utils/excelExport';
import { toast } from 'sonner';
import { generateTripICS } from '@/utils/tripIcsExport';
import { downloadICSFile } from '@/utils/icsExport';
import CalendarImportGuideModal from '../CalendarImportGuideModal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { TripStopWeatherData } from '@/hooks/useTripStopWeather';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { useRoutesBetweenStops } from '@/hooks/useRoutesBetweenStops';
import { DriveTimeConnector } from './DriveTimeConnector';
import { formatDriveDuration, formatDriveDistance } from '@/utils/routeFormatting';
import { lookupBortleBatch } from '@/utils/bortleLookup';
import { edgeFunctionHeaders } from '@/lib/edgeFunctionHeaders';

interface TripDetailViewProps {
  trip: TravelItineraryWithStops;
  onBack: () => void;
}

export const TripDetailView: React.FC<TripDetailViewProps> = ({ trip, onBack }) => {
  const { itineraries, updateItinerary, addStop, updateStop, deleteStop, shareTrip, unshareTrip } = useTravelItineraries();
  const { user, isSubscribed } = useAuth();
  const { preferredUnits, formatTemperature, formatPrecipitation, formatWindSpeed } = useUnitPreferences();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(trip.name);
  const [isAddStopOpen, setIsAddStopOpen] = useState(false);
  const [editingStop, setEditingStop] = useState<string | null>(null);
  const [showImportGuide, setShowImportGuide] = useState(false);
  const [icsFilename, setIcsFilename] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [locationBortles, setLocationBortles] = useState<Record<string, number>>({});

  // Refresh trip data after operations
  const [currentTrip, setCurrentTrip] = useState(trip);
  const { routes, loading: routesLoading } = useRoutesBetweenStops(currentTrip.stops);

  React.useEffect(() => {
    // Find the current trip from the fetched itineraries to get fresh data
    const updatedTrip = itineraries.find(i => i.id === trip.id);
    if (updatedTrip) {
      setCurrentTrip(updatedTrip);
      setEditedName(updatedTrip.name);
    } else {
      // Fallback to prop if not yet in itineraries
      setCurrentTrip(trip);
      setEditedName(trip.name);
    }
  }, [trip, itineraries]);

  // Look up Bortle class for each trip stop from PMTiles light pollution data
  React.useEffect(() => {
    const stops = currentTrip.stops.filter((s) => s.latitude && s.longitude);
    if (stops.length === 0) return;

    lookupBortleBatch(
      stops.map((s) => ({ key: s.id, lat: s.latitude, lng: s.longitude })),
    ).then((bortles) => {
      if (Object.keys(bortles).length > 0) {
        setLocationBortles(bortles);
      }
    });
  }, [currentTrip.stops]);

  const handleSaveName = async () => {
    if (editedName.trim() && editedName !== currentTrip.name) {
      await updateItinerary(currentTrip.id, { name: editedName.trim() });
    }
    setIsEditingName(false);
  };

  const handleAddStop = async (stopData: {
    location_name: string;
    latitude: number;
    longitude: number;
    arrival_date: string;
    departure_date: string;
    notes?: string;
  }) => {
    const nextOrder = currentTrip.stops.length + 1;
    await addStop(currentTrip.id, {
      ...stopData,
      stop_order: nextOrder,
      notes: stopData.notes || null
    });
    setIsAddStopOpen(false);
  };

  const handleEditStop = async (stopId: string, stopData: {
    location_name: string;
    latitude: number;
    longitude: number;
    arrival_date: string;
    departure_date: string;
    notes?: string;
  }) => {
    await updateStop(stopId, {
      ...stopData,
      notes: stopData.notes || null
    });
    setEditingStop(null);
  };

  const handleDeleteStop = async (stopId: string) => {
    await deleteStop(stopId);
  };

  const getDateRange = () => {
    if (currentTrip.stops.length === 0) return null;
    
    const dates = currentTrip.stops.flatMap(stop => [
      parseISO(stop.arrival_date),
      parseISO(stop.departure_date)
    ]);
    
    const earliest = new Date(Math.min(...dates.map(d => d.getTime())));
    const latest = new Date(Math.max(...dates.map(d => d.getTime())));
    
    return `${format(earliest, 'MMMM d')} - ${format(latest, 'MMMM d, yyyy')}`;
  };

  const createVisibilityBars = (rating: number): string => {
    const filledBars = '█'.repeat(rating);
    const emptyBars = '░'.repeat(10 - rating);
    return filledBars + emptyBars;
  };

  const getMoonPhaseName = (phase: number): string => {
    const moonPercent = Math.round(phase * 100);
    if (moonPercent <= 5) return 'New Moon';
    if (moonPercent <= 25) return 'Waxing Crescent';
    if (moonPercent <= 45) return 'First Quarter';
    if (moonPercent <= 55) return 'Waxing Gibbous';
    if (moonPercent <= 95) return 'Full Moon';
    return 'Waning';
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { /* fallback below */ }
    // Fallback for non-secure contexts
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  };

  const generateTripFilename = (ext: string): string => {
    const safeName = currentTrip.name.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
    return `Trip_${safeName}_${format(new Date(), 'yyyy-MM-dd')}.${ext}`;
  };

  const calculateNightData = (stop: typeof currentTrip.stops[0]) => {
    const arrival = parseISO(stop.arrival_date);
    const departure = parseISO(stop.departure_date);
    const lastNight = subDays(departure, 1);
    const nights = arrival <= lastNight ? eachDayOfInterval({ start: arrival, end: lastNight }) : [];

    const location = {
      name: stop.location_name,
      latitude: Number(stop.latitude),
      longitude: Number(stop.longitude)
    };

    // Pre-compute unique months to avoid redundant calls
    const monthCache = new Map<string, DayData[]>();
    nights.forEach(night => {
      const key = `${night.getMonth()}-${night.getFullYear()}`;
      if (!monthCache.has(key)) {
        monthCache.set(key, calculateMonthData(location, night.getMonth(), night.getFullYear()));
      }
    });

    return nights.map(night => {
      const key = `${night.getMonth()}-${night.getFullYear()}`;
      const monthData = monthCache.get(key) || [];
      const dayData = monthData.find(d => d.date === night.getDate());
      return {
        ...dayData,
        isoDate: format(night, 'yyyy-MM-dd'),
        displayDate: format(night, 'MMM d, yyyy'),
      };
    }).filter(d => d.moonPhase !== undefined);
  };

  const fetchWeatherForStops = async (): Promise<Record<string, TripStopWeatherData>> => {
    if (!user || !isSubscribed) return {};
    const weatherMap: Record<string, TripStopWeatherData> = {};
    let failedStops = 0;
    await Promise.all(
      currentTrip.stops.map(async (stop) => {
        try {
          const response = await supabase.functions.invoke('get-trip-stop-weather', {
            body: {
              latitude: Number(stop.latitude),
              longitude: Number(stop.longitude),
              arrivalDate: stop.arrival_date,
              departureDate: stop.departure_date,
              userId: user.id,
            },
          });

          if (response.error) {
            // Extract real error message from FunctionsHttpError body
            const errorContext = response.error as { context?: { body?: BodyInit | null } };
            let realMessage = response.error.message;
            try {
              if (errorContext?.context?.body) {
                const bodyText = await new Response(errorContext.context.body).text();
                const parsed = JSON.parse(bodyText);
                if (parsed?.error) realMessage = parsed.error;
              }
            } catch { /* ignore parse failures */ }
            console.warn(`Weather fetch failed for ${stop.location_name}:`, realMessage);
            failedStops++;
            return;
          }

          const data = response.data;
          if (data && !data.error) {
            weatherMap[stop.id] = data as TripStopWeatherData;
          } else {
            console.warn(`Weather returned error for ${stop.location_name}:`, data?.error || 'empty response');
            failedStops++;
          }
        } catch {
          // Weather is supplementary — don't block export
          failedStops++;
        }
      })
    );
    if (failedStops > 0 && Object.keys(weatherMap).length === 0) {
      toast.warning('Weather data unavailable — export will proceed without weather columns');
    } else if (failedStops > 0) {
      toast.warning(`Weather unavailable for ${failedStops} stop${failedStops > 1 ? 's' : ''}`);
    }
    return weatherMap;
  };

  const getCloudCoverLabel = (cc: number): string => {
    if (cc < 25) return 'Excellent';
    if (cc < 50) return 'Good';
    if (cc < 75) return 'Fair';
    return 'Poor';
  };

  const exportToExcel = async () => {
    if (currentTrip.stops.length === 0) {
      toast.error('No stops to export');
      return;
    }

    // Fetch weather for all stops in parallel
    const weatherMap = await fetchWeatherForStops();

    const sheets: ExcelSheet[] = [];

    // Sheet 1: Trip Overview
    const dateRange = getDateRange() || 'No dates';
    const totalNights = currentTrip.stops.reduce((acc, stop) => {
      const arrival = parseISO(stop.arrival_date);
      const departure = parseISO(stop.departure_date);
      const lastNight = subDays(departure, 1);
      return acc + (arrival <= lastNight ? eachDayOfInterval({ start: arrival, end: lastNight }).length : 0);
    }, 0);

    const overviewData = [
      ['TRAVEL ITINERARY EXPORT'],
      [''],
      ['Trip Name:', currentTrip.name],
      ['Date Range:', dateRange],
      ['Total Stops:', currentTrip.stops.length],
      ['Total Nights:', totalNights],
      [''],
      ['HOW TO USE THIS DATA'],
      ['• Rating shown as bars: ██████████ = 10/10 (perfect), ░░░░░░░░░░ = 0/10 (not visible)'],
      ['• 7+ bars: Excellent conditions for Milky Way photography'],
      ['• 4-6 bars: Moderate conditions, some moon interference'],
      ['• 0-3 bars: Poor conditions or galactic core not visible'],
      ['• Core Start/End times indicate when the galactic core is visible in darkness'],
      ['• Cloud cover clarity: Excellent (<25%), Good (25-49%), Fair (50-74%), Poor (75%+)']
    ];
    sheets.push({ name: 'Trip Overview', data: overviewData });

    // Sheet 2: Itinerary Summary (with weather)
    const itineraryHeaders = ['Stop #', 'Location', 'Arrival', 'Departure', 'Nights', 'Drive Time', 'Drive Distance', 'Best Night', 'Best Rating', 'Cloud Cover', 'Clarity', 'Temp Range', 'Precip', 'Latitude', 'Longitude', 'Notes'];
    const sortedStopsForExport = [...currentTrip.stops].sort((a, b) => a.stop_order - b.stop_order);
    const itineraryRows = sortedStopsForExport
      .map((stop, idx) => {
        const nightData = calculateNightData(stop);
        const bestNight = nightData.reduce<(ReturnType<typeof calculateNightData>[number] & { rating: number }) | null>((best, night) => {
          const rating = getVisibilityRating(night as DayData);
          return rating > (best?.rating || 0) ? { ...night, rating } : best;
        }, null);

        // Drive time from previous stop
        const prevStop = idx > 0 ? sortedStopsForExport[idx - 1] : null;
        const routeSegment = prevStop
          ? routes.find(r => r.fromStopId === prevStop.id && r.toStopId === stop.id)
          : null;
        const driveTime = routeSegment ? formatDriveDuration(routeSegment.duration) : '--';
        const driveDist = routeSegment ? formatDriveDistance(routeSegment.distance, preferredUnits) : '--';

        const weather = weatherMap[stop.id];
        let cloudCover = '--';
        let clarity = '--';
        let tempRange = '--';
        let precip = '--';

        if (weather?.source === 'forecast' && weather.daily?.length) {
          const avgCloud = Math.round(weather.daily.reduce((s, d) => s + d.cloud_cover, 0) / weather.daily.length);
          const minTemp = Math.min(...weather.daily.map(d => d.temperature_min));
          const maxTemp = Math.max(...weather.daily.map(d => d.temperature_max));
          const totalPrecip = weather.daily.reduce((s, d) => s + d.precipitation, 0);
          cloudCover = `${avgCloud}%`;
          clarity = getCloudCoverLabel(avgCloud);
          tempRange = `${formatTemperature(minTemp, false)}–${formatTemperature(maxTemp)}`;
          precip = formatPrecipitation(totalPrecip);
        } else if (weather?.source === 'historical' && weather.monthly?.length) {
          const m = weather.monthly[0];
          cloudCover = `${m.cloud_cover_avg}%`;
          clarity = getCloudCoverLabel(m.cloud_cover_avg);
          tempRange = `${formatTemperature(m.temperature_min, false)}–${formatTemperature(m.temperature_max)}`;
          precip = `${formatPrecipitation(m.precipitation_avg)}/day`;
        }

        return [
          idx + 1,
          stop.location_name,
          format(parseISO(stop.arrival_date), 'MMM d, yyyy'),
          format(parseISO(stop.departure_date), 'MMM d, yyyy'),
          nightData.length,
          driveTime,
          driveDist,
          bestNight?.displayDate || 'N/A',
          bestNight?.rating ? createVisibilityBars(bestNight.rating) : 'N/A',
          cloudCover,
          clarity,
          tempRange,
          precip,
          Number(stop.latitude).toFixed(4),
          Number(stop.longitude).toFixed(4),
          stop.notes || ''
        ];
      });
    sheets.push({ name: 'Itinerary', data: [itineraryHeaders, ...itineraryRows] });

    // Sheet 3+: Detailed per-stop data (with per-night weather for forecasts)
    currentTrip.stops
      .sort((a, b) => a.stop_order - b.stop_order)
      .forEach((stop, idx) => {
        const nightData = calculateNightData(stop);
        const weather = weatherMap[stop.id];
        const hasForecast = weather?.source === 'forecast' && weather.daily?.length;
        const forecastByDate = new Map(
          (weather?.daily || []).map(d => [d.date, d])
        );

        const headers = hasForecast
          ? ['Date', 'Sunrise', 'Sunset', 'Core Start', 'Core End', 'Moon %', 'Moon Phase', 'Visibility', 'Cloud Cover', 'Temp', 'Precip', 'Wind']
          : ['Date', 'Sunrise', 'Sunset', 'Core Start', 'Core End', 'Moon %', 'Moon Phase', 'Visibility'];

        const rows = nightData.map(night => {
          const rating = getVisibilityRating(night as DayData);
          const baseRow = [
            night.displayDate,
            night.sunrise || '--',
            night.sunset || '--',
            night.galacticCoreStart || '--',
            night.galacticCoreEnd || '--',
            night.moonPhase ? `${Math.round(night.moonPhase * 100)}%` : '--',
            night.moonPhase ? getMoonPhaseName(night.moonPhase) : '--',
            createVisibilityBars(rating)
          ];

          if (hasForecast) {
            const fc = forecastByDate.get(night.isoDate);
            baseRow.push(
              fc ? `${fc.cloud_cover}%` : '--',
              fc ? `${formatTemperature(fc.temperature_min, false)}–${formatTemperature(fc.temperature_max)}` : '--',
              fc ? formatPrecipitation(fc.precipitation) : '--',
              fc ? formatWindSpeed(fc.wind_speed_max) : '--'
            );
          }

          return baseRow;
        });

        // Add historical weather note if applicable
        const sheetData = [headers, ...rows];
        if (weather?.source === 'historical' && weather.monthly?.length) {
          const m = weather.monthly[0];
          sheetData.push([]);
          sheetData.push([`${m.monthName} Historical Avg: Cloud ${m.cloud_cover_avg}% (${getCloudCoverLabel(m.cloud_cover_avg)}), Temp ${formatTemperature(m.temperature_min, false)}–${formatTemperature(m.temperature_max)}, Precip ${formatPrecipitation(m.precipitation_avg)}/day, Wind ${formatWindSpeed(m.wind_speed_avg)}`]);
        }

        const sheetName = `${idx + 1} - ${stop.location_name}`.substring(0, 31);
        sheets.push({ name: sheetName, data: sheetData });
      });

    // Download
    const filename = generateTripFilename('xlsx');
    await writeExcelFile(sheets, filename);
    toast.success(`Exported ${filename}`);
  };

  const exportToCSV = async () => {
    if (currentTrip.stops.length === 0) {
      toast.error('No stops to export');
      return;
    }

    const weatherMap = await fetchWeatherForStops();

    const tempUnit = preferredUnits === 'imperial' ? '°F' : '°C';
    const precipUnit = preferredUnits === 'imperial' ? 'in' : 'mm';
    const headers = ['Stop #', 'Location', 'Date', 'Core Start', 'Core End', 'Moon %', 'Rating', 'Cloud Cover', `Temp Min (${tempUnit})`, `Temp Max (${tempUnit})`, `Precip (${precipUnit})`, 'Notes'];
    const rows: string[][] = [];

    currentTrip.stops
      .sort((a, b) => a.stop_order - b.stop_order)
      .forEach((stop, idx) => {
        const nightData = calculateNightData(stop);
        const weather = weatherMap[stop.id];
        const forecastByDate = new Map(
          (weather?.daily || []).map(d => [d.date, d])
        );
        const historicalMonth = weather?.source === 'historical' && weather.monthly?.length ? weather.monthly[0] : null;

        nightData.forEach(night => {
          const rating = getVisibilityRating(night as DayData);
          const fc = forecastByDate.get(night.isoDate);

          rows.push([
            String(idx + 1),
            stop.location_name,
            night.displayDate,
            night.galacticCoreStart || '--',
            night.galacticCoreEnd || '--',
            night.moonPhase ? `${Math.round(night.moonPhase * 100)}%` : '--',
            String(rating),
            fc ? `${fc.cloud_cover}%` : historicalMonth ? `${historicalMonth.cloud_cover_avg}% avg` : '--',
            fc ? formatTemperature(fc.temperature_min, false) : historicalMonth ? formatTemperature(historicalMonth.temperature_min, false) : '--',
            fc ? formatTemperature(fc.temperature_max, false) : historicalMonth ? formatTemperature(historicalMonth.temperature_max, false) : '--',
            fc ? formatPrecipitation(fc.precipitation, false) : historicalMonth ? formatPrecipitation(historicalMonth.precipitation_avg, false) : '--',
            stop.notes || ''
          ]);
        });
      });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = generateTripFilename('csv');
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${generateTripFilename('csv')}`);
  };

  const tripBestNight = useMemo(() => {
    if (currentTrip.stops.length === 0) return null;

    let best: { stopName: string; date: string; rating: number; quality: number; coreStart: string; coreEnd: string } | null = null;

    currentTrip.stops.forEach(stop => {
      const nightData = calculateNightData(stop);
      nightData.forEach(night => {
        const rating = getVisibilityRating(night as DayData);
        if (rating > (best?.rating || 0)) {
          const quality = getShootingQualityScore({
            mwVisibilityRating: rating,
            maxElevation: (night as DayData).maxElevation,
            darknessWindowMinutes: getDarknessWindowMinutes(
              (night as DayData).astroTwilightEvening,
              (night as DayData).astroTwilightMorning
            ),
            bortleClass: locationBortles[stop.id] ?? undefined,
          });
          best = {
            stopName: stop.location_name,
            date: night.displayDate,
            rating,
            quality,
            coreStart: (night as DayData).galacticCoreStart || '--',
            coreEnd: (night as DayData).galacticCoreEnd || '--',
          };
        }
      });
    });

    return best;
  }, [currentTrip, locationBortles]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <Input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="text-2xl font-semibold h-auto py-1"
                autoFocus
              />
              <Button size="icon" variant="ghost" onClick={handleSaveName}>
                <Check className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => {
                setEditedName(currentTrip.name);
                setIsEditingName(false);
              }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-foreground">{currentTrip.name}</h1>
              <Button size="icon" variant="ghost" onClick={() => setIsEditingName(true)}>
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          )}
          {getDateRange() && (
            <p className="text-muted-foreground mt-1">{getDateRange()}</p>
          )}
        </div>
      </div>

      {/* Trip Overview Map */}
      {currentTrip.stops.length > 0 && (
        <Suspense fallback={<div className="h-[350px] rounded-xl bg-muted animate-pulse" />}>
          <TripOverviewMap
            stops={currentTrip.stops.map(stop => {
              const nightData = calculateNightData(stop);
              const ratings = nightData.map(n => getVisibilityRating(n as DayData));
              const bestRating = ratings.length > 0 ? Math.max(...ratings) : 0;
              const goodNights = ratings.filter(r => r >= 4).length;
              return {
                ...stop,
                bestRating,
                totalNights: nightData.length,
                goodNights,
              };
            })}
            routeGeometries={routes.map(r => {
              const fromStop = currentTrip.stops.find(s => s.id === r.fromStopId);
              const toStop = currentTrip.stops.find(s => s.id === r.toStopId);
              return {
                fromStopOrder: fromStop?.stop_order ?? 0,
                toStopOrder: toStop?.stop_order ?? 0,
                coordinates: r.geometry.coordinates.map(
                  ([lng, lat]) => [lat, lng] as [number, number]
                ),
              };
            })}
          />
        </Suspense>
      )}

      {tripBestNight && tripBestNight.rating >= 5 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex items-center gap-3 py-4">
            <Star className="w-5 h-5 text-primary fill-primary shrink-0" />
            <div className="text-sm">
              <span className="font-medium">Trip best night:</span>{' '}
              <span>{tripBestNight.date}</span>{' '}
              <span className="text-muted-foreground">at {tripBestNight.stopName}</span>{' '}
              <span className="text-muted-foreground">•</span>{' '}
              <span>MW {tripBestNight.rating}/10</span>{' '}
              <span className="text-muted-foreground">•</span>{' '}
              <span className={getQualityLabel(tripBestNight.quality).color}>
                Quality {tripBestNight.quality}/10
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stops Timeline */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-medium">Itinerary</h2>
          <div className="flex items-center gap-2">
            {currentTrip.stops.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={generatingPdf}
                  onClick={async () => {
                    setGeneratingPdf(true);
                    const routeData = routes.map(r => {
                      const fromStop = currentTrip.stops.find(s => s.id === r.fromStopId);
                      const toStop = currentTrip.stops.find(s => s.id === r.toStopId);
                      return {
                        from_order: fromStop?.stop_order ?? 0,
                        to_order: toStop?.stop_order ?? 0,
                        duration: r.duration,
                        distance: r.distance,
                      };
                    });
                    try {
                      const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-trip-pdf`, {
                        method: 'POST',
                        headers: await edgeFunctionHeaders(),
                        body: JSON.stringify({
                          name: currentTrip.name,
                          units: preferredUnits,
                          routes: routeData,
                          stops: currentTrip.stops.map(s => ({
                            location_name: s.location_name,
                            latitude: s.latitude,
                            longitude: s.longitude,
                            arrival_date: s.arrival_date,
                            departure_date: s.departure_date,
                            notes: s.notes,
                            stop_order: s.stop_order
                          }))
                        })
                      });
                      if (!response.ok) throw new Error('Failed to generate PDF');
                      const blob = new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = generateTripFilename('pdf');
                      link.click();
                      URL.revokeObjectURL(url);
                      toast.success('PDF downloaded!');
                    } catch (error) {
                      toast.error('Failed to generate PDF');
                    } finally {
                      setGeneratingPdf(false);
                    }
                  }}
                  className="gap-2"
                >
                  {generatingPdf ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  {generatingPdf ? 'Generating...' : 'PDF'}
                </Button>
                <Button variant="outline" size="sm" onClick={exportToExcel} className="gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </Button>
                <Button variant="outline" size="sm" onClick={exportToCSV} className="gap-2">
                  <Download className="w-4 h-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  if (currentTrip.stops.length === 0) {
                    toast.error('No stops to export');
                    return;
                  }
                  const { icsContent, eventCount, filename } = generateTripICS(currentTrip);
                  downloadICSFile(icsContent, filename);
                  setIcsFilename(filename);
                  setShowImportGuide(true);
                  toast.success(`Calendar exported with ${eventCount} stop${eventCount !== 1 ? 's' : ''}`);
                }} className="gap-2">
                  <CalendarPlus className="w-4 h-4" />
                  Calendar
                </Button>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Backpack className="w-4 h-4" />
                      Packing List
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Packing List for {currentTrip.name}</DialogTitle>
                    </DialogHeader>
                    <PackingList stops={currentTrip.stops} />
                  </DialogContent>
                </Dialog>
                {currentTrip.is_public ? (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const url = `${window.location.origin}/trip/${currentTrip.slug}`;
                        const copied = await copyToClipboard(url);
                        if (copied) {
                          toast.success('Link copied to clipboard');
                        } else {
                          toast.info(url, { duration: 10000 });
                        }
                      }}
                      className="gap-2 border-primary/30 text-primary"
                    >
                      <Link2 className="w-4 h-4" />
                      Copy Link
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        await unshareTrip(currentTrip.id);
                      }}
                      className="gap-1 text-muted-foreground"
                      title="Make private"
                    >
                      <Lock className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const slug = await shareTrip(currentTrip.id);
                      if (slug) {
                        const url = `${window.location.origin}/trip/${slug}`;
                        const copied = await copyToClipboard(url);
                        if (copied) {
                          toast.success('Trip shared! Link copied to clipboard');
                        } else {
                          toast.success('Trip shared!');
                          toast.info(url, { duration: 10000 });
                        }
                      }
                    }}
                    className="gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </Button>
                )}
              </>
            )}
            <Button onClick={() => setIsAddStopOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Add Stop
            </Button>
          </div>
        </div>

        {currentTrip.stops.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">
                No stops added yet. Add your first destination to start planning.
              </p>
              <Button onClick={() => setIsAddStopOpen(true)} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Add First Stop
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
            
            {/* Stops */}
            <div className="space-y-4">
              {currentTrip.stops
                .sort((a, b) => a.stop_order - b.stop_order)
                .map((stop, index, sortedStops) => {
                  const prevStop = index > 0 ? sortedStops[index - 1] : null;
                  const routeSegment = prevStop
                    ? routes.find(r => r.fromStopId === prevStop.id && r.toStopId === stop.id)
                    : null;

                  return (
                    <React.Fragment key={stop.id}>
                      {index > 0 && (
                        <DriveTimeConnector
                          duration={routeSegment?.duration ?? null}
                          distance={routeSegment?.distance ?? null}
                          loading={routesLoading}
                          units={preferredUnits}
                        />
                      )}
                      <TripStopCard
                        stop={stop}
                        stopNumber={index + 1}
                        isLast={index === currentTrip.stops.length - 1}
                        onEdit={() => setEditingStop(stop.id)}
                        onDelete={() => handleDeleteStop(stop.id)}
                        bortleClass={locationBortles[stop.id] ?? null}
                      />
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Add Stop Dialog */}
      <TripStopDialog
        open={isAddStopOpen}
        onOpenChange={setIsAddStopOpen}
        onSave={handleAddStop}
      />

      {/* Edit Stop Dialog */}
      {editingStop && (
        <TripStopDialog
          open={!!editingStop}
          onOpenChange={(open) => !open && setEditingStop(null)}
          onSave={(data) => handleEditStop(editingStop, data)}
          initialData={currentTrip.stops.find(s => s.id === editingStop)}
        />
      )}

      <CalendarImportGuideModal
        isOpen={showImportGuide}
        onClose={() => setShowImportGuide(false)}
        fileName={icsFilename}
      />
    </div>
  );
};
